import type { MemAgentSlackConnectSource } from "@/domains/metrics/tracked-event";
import {
  SubscriptionPlanKind,
  type SubscriptionPlanKind as SubscriptionPlanKindType,
} from "@/store/subscriptions/types";

const MEM_AGENT_SLACK_CONNECTED_MODAL_PENDING_KEY = "mem.mem_agent.slack_connected_modal_pending";

export interface MemAgentSlackConnectedModalPendingContext {
  source: MemAgentSlackConnectSource;
  is_setup: boolean;
  original_plan_kind?: SubscriptionPlanKindType;
  original_effective_plan_kind?: SubscriptionPlanKindType;
}

const DEFAULT_PENDING_CONTEXT: MemAgentSlackConnectedModalPendingContext = {
  source: "settings",
  is_setup: false,
};

const isMemAgentSlackConnectSource = (source: unknown): source is MemAgentSlackConnectSource => {
  return source === "settings" || source === "proactive_checkout";
};

const parseSubscriptionPlanKind = (value: unknown): SubscriptionPlanKindType | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  return (Object.values(SubscriptionPlanKind) as string[]).includes(value)
    ? (value as SubscriptionPlanKindType)
    : undefined;
};

const parsePendingContext = (
  rawValue: string
): MemAgentSlackConnectedModalPendingContext | null => {
  if (rawValue === "true") {
    return DEFAULT_PENDING_CONTEXT;
  }

  try {
    const parsed = JSON.parse(rawValue) as {
      source?: unknown;
      is_setup?: unknown;
      isSetup?: unknown;
      original_plan_kind?: unknown;
      original_effective_plan_kind?: unknown;
    };

    return {
      source: isMemAgentSlackConnectSource(parsed.source)
        ? parsed.source
        : DEFAULT_PENDING_CONTEXT.source,
      is_setup:
        typeof parsed.is_setup === "boolean"
          ? parsed.is_setup
          : typeof parsed.isSetup === "boolean"
            ? parsed.isSetup
            : DEFAULT_PENDING_CONTEXT.is_setup,
      original_plan_kind: parseSubscriptionPlanKind(parsed.original_plan_kind),
      original_effective_plan_kind: parseSubscriptionPlanKind(parsed.original_effective_plan_kind),
    };
  } catch {
    return null;
  }
};

export const writeMemAgentSlackConnectedModalPending = (
  context: MemAgentSlackConnectedModalPendingContext = DEFAULT_PENDING_CONTEXT
): void => {
  window.localStorage.setItem(MEM_AGENT_SLACK_CONNECTED_MODAL_PENDING_KEY, JSON.stringify(context));
};

export const readMemAgentSlackConnectedModalPending =
  (): MemAgentSlackConnectedModalPendingContext | null => {
    const rawValue = window.localStorage.getItem(MEM_AGENT_SLACK_CONNECTED_MODAL_PENDING_KEY);

    if (!rawValue) {
      return null;
    }

    return parsePendingContext(rawValue);
  };

export const clearMemAgentSlackConnectedModalPending = (): void => {
  window.localStorage.removeItem(MEM_AGENT_SLACK_CONNECTED_MODAL_PENDING_KEY);
};

export const consumeMemAgentSlackConnectedModalPending =
  (): MemAgentSlackConnectedModalPendingContext | null => {
    const pendingContext = readMemAgentSlackConnectedModalPending();

    clearMemAgentSlackConnectedModalPending();

    return pendingContext;
  };
